import { useEffect, useRef, useState, useCallback } from "react";
import { resolveImg } from "@/lib/api";
import { RotateCw } from "lucide-react";
import { FALLBACK_IMG } from "./SafeImage";

/**
 * Spin360Viewer — image-sequence spin viewer using Pointer Events.
 *
 * Fixes for repeated-drag freeze bug:
 *  - Single pointer-event pipeline (mouse + touch + pen unified)
 *  - setPointerCapture on down; onLostPointerCapture / onPointerCancel reset drag
 *  - touch-action: none prevents browser gesture from cancelling our pointer
 *  - Pointer id tracked so a second finger/pointer cannot corrupt state
 *  - onPointerUp always fires (capture guarantees) so dragging.current can't get stuck
 *  - No requestAnimationFrame / throttle state to fall out of sync
 */
export default function Spin360Viewer({ images = [] }) {
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const dragging = useRef(false);
  const pointerId = useRef(null);
  const startX = useRef(0);
  const startIdx = useRef(0);
  const containerRef = useRef(null);

  // Lazy-preload sequence
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.all(images.map((src) => new Promise((res) => {
        const im = new Image();
        im.onload = im.onerror = res;
        im.src = resolveImg(src);
      })));
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [images]);

  const endDrag = useCallback((e) => {
    if (!dragging.current) return;
    dragging.current = false;
    const el = containerRef.current;
    if (el && pointerId.current != null) {
      try { el.releasePointerCapture(pointerId.current); } catch {}
    }
    pointerId.current = null;
  }, []);

  if (!images.length) return null;

  const onPointerDown = (e) => {
    // Only accept the first active pointer; ignore secondary touches.
    if (dragging.current) return;
    dragging.current = true;
    pointerId.current = e.pointerId;
    startX.current = e.clientX;
    startIdx.current = index;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };

  const onPointerMove = (e) => {
    if (!dragging.current || e.pointerId !== pointerId.current) return;
    // Prevent page scroll from stealing the gesture (belt-and-suspenders with touch-action:none)
    if (e.cancelable) e.preventDefault?.();
    const dx = e.clientX - startX.current;
    const step = Math.trunc(dx / 8);
    let next = (startIdx.current - step) % images.length;
    if (next < 0) next += images.length;
    if (next !== index) setIndex(next);
  };

  return (
    <div
      ref={containerRef}
      className="relative bg-[color:var(--forest-2)] aspect-square select-none corner-frame cursor-grab active:cursor-grabbing"
      style={{ touchAction: "none" }}
      data-testid="spin360-viewer"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={endDrag}
    >
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center text-gold">
          <RotateCw className="animate-spin" size={22} />
        </div>
      )}
      <img
        src={resolveImg(images[index])}
        onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
        alt="360° view"
        className="w-full h-full object-contain pointer-events-none"
        draggable={false}
      />
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-gold font-caption text-[10px] bg-[rgba(9,18,13,0.7)] px-3 py-1 border border-brass/40 pointer-events-none">
        Drag to rotate
      </div>
    </div>
  );
}
